/**
 * ChroniclePickerModal — Select a chronicle to share a news/feed item to.
 *
 * Shows chronicles the user owns or can edit, with search.
 * On selection, calls the add-news-link endpoint to create a
 * ChronicleExternalLink with OG metadata copied from the news_items record.
 *
 * DEV-516: Share to Chronicle
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { fetchUserChronicles, addNewsLinkToChronicle, type ChronicleSummary } from '../../api/chronicles';
import { mediaSrc } from '../../api/mediaUrl';
import styles from './ChroniclePickerModal.module.css';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** GUID of the news item to share. */
  newsItemGuid: string;
  /** Title of the article being shared (for display). */
  articleTitle: string;
  /** Current user GUID — used to fetch their chronicles. */
  currentUserGuid: string;
  /** Callback after successful share. */
  onShared?: (chronicleGuid: string, chronicleTitle: string) => void;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const ChroniclePickerModal: React.FC<Props> = ({
  isOpen,
  onClose,
  newsItemGuid,
  articleTitle,
  currentUserGuid,
  onShared,
}) => {
  const { t } = useAppTranslation('pages');
  const [chronicles, setChronicles] = useState<ChronicleSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // ── Load chronicles ─────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !currentUserGuid) return;
    let cancelled = false;

    setLoading(true);
    setError(null);
    setSuccess(null);
    setSearch('');

    fetchUserChronicles(currentUserGuid)
      .then((items) => {
        if (!cancelled) {
          // Show drafts and published — user may want to add links to drafts
          setChronicles(items);
        }
      })
      .catch(() => {
        if (!cancelled) setError(t('shareToChronicle.loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, currentUserGuid, t]);

  // Auto-focus search when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // ── Filter ──────────────────────────────────────────────
  const filtered = search.trim()
    ? chronicles.filter((c) =>
        c.title.toLowerCase().includes(search.toLowerCase()) ||
        (c.subtitle?.toLowerCase().includes(search.toLowerCase()) ?? false)
      )
    : chronicles;

  // ── Share handler ───────────────────────────────────────
  const handleSelect = useCallback(async (chronicle: ChronicleSummary) => {
    setSubmitting(chronicle.chronicleGuid);
    setError(null);

    try {
      await addNewsLinkToChronicle(chronicle.chronicleGuid, newsItemGuid);
      setSuccess(chronicle.title);
      onShared?.(chronicle.chronicleGuid, chronicle.title);
      // Auto-close after brief success display
      setTimeout(() => onClose(), 1500);
    } catch (err: any) {
      if (err?.status === 409 || err?.message?.includes('409')) {
        setError(t('shareToChronicle.alreadyLinked'));
      } else if (err?.status === 403) {
        setError(t('shareToChronicle.noPermission'));
      } else {
        setError(t('shareToChronicle.shareFailed'));
      }
    } finally {
      setSubmitting(null);
    }
  }, [newsItemGuid, onClose, onShared, t]);

  // ── Render ──────────────────────────────────────────────
  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>{t('shareToChronicle.title')}</h2>
          <button type="button" className={styles.closeBtn} onClick={onClose}>
            ×
          </button>
        </div>

        {/* Article being shared */}
        <div className={styles.articlePreview}>
          <span className={styles.articleLabel}>{t('shareToChronicle.sharing')}</span>
          <span className={styles.articleTitle}>{articleTitle}</span>
        </div>

        {/* Search */}
        <div className={styles.searchBox}>
          <input
            ref={searchRef}
            type="text"
            className={styles.searchInput}
            placeholder={t('shareToChronicle.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Content area */}
        <div className={styles.body}>
          {loading && (
            <div className={styles.loadingState}>{t('shareToChronicle.loading')}</div>
          )}

          {!loading && error && !success && (
            <div className={styles.errorState}>{error}</div>
          )}

          {!loading && success && (
            <div className={styles.successState}>
              {t('shareToChronicle.success', { chronicle: success })}
            </div>
          )}

          {!loading && !success && filtered.length === 0 && (
            <div className={styles.emptyState}>
              {chronicles.length === 0
                ? t('shareToChronicle.noChronicles')
                : t('shareToChronicle.noResults')}
            </div>
          )}

          {!loading && !success && filtered.map((c) => (
            <button
              key={c.chronicleGuid}
              type="button"
              className={styles.chronicleItem}
              disabled={submitting !== null}
              onClick={() => handleSelect(c)}
            >
              {c.heroImageUrl ? (
                <img
                  className={styles.chronicleThumbnail}
                  src={mediaSrc(c.heroOriginalUrl || c.heroImageUrl) ?? undefined}
                  alt=""
                />
              ) : (
                <div className={styles.chronicleThumbnailPlaceholder}>📋</div>
              )}
              <div className={styles.chronicleInfo}>
                <div className={styles.chronicleName}>{c.title}</div>
                {c.subtitle && (
                  <div className={styles.chronicleSubtitle}>{c.subtitle}</div>
                )}
                <div className={styles.chronicleMeta}>
                  <span className={`${styles.statusBadge} ${c.status === 'published' ? styles.published : styles.draft}`}>
                    {c.status === 'published'
                      ? t('shareToChronicle.statusPublished')
                      : t('shareToChronicle.statusDraft')}
                  </span>
                  {c.categoryKey && (
                    <span className={styles.categoryBadge}>{c.categoryKey}</span>
                  )}
                </div>
              </div>
              {submitting === c.chronicleGuid && (
                <div className={styles.spinner} />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChroniclePickerModal;
